import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { haptic } from '../../../shared/utils/haptics';

// Niveaux d'humeur
export const MOOD_LEVELS = {
  1: { emoji: '😫', label: 'Très difficile', color: '#dc2626', mode: 'gentle' },
  2: { emoji: '😔', label: 'Difficile', color: '#f97316', mode: 'gentle' },
  3: { emoji: '😐', label: 'Neutre', color: '#eab308', mode: 'normal' },
  4: { emoji: '🙂', label: 'Bien', color: '#22c55e', mode: 'normal' },
  5: { emoji: '😊', label: 'Très bien', color: '#10b981', mode: 'productive' }
};

// Niveaux d'énergie
export const ENERGY_LEVELS = {
  1: { icon: '🔋', label: 'Épuisé', bars: 1 },
  2: { icon: '🔋', label: 'Fatigué', bars: 2 },
  3: { icon: '🔋', label: 'Normal', bars: 3 },
  4: { icon: '🔋', label: 'Énergique', bars: 4 }
};

// Messages d'encouragement selon l'humeur
export const MOOD_MESSAGES = {
  gentle: [
    "Aujourd'hui, concentre-toi sur l'essentiel. Tu fais de ton mieux. 💙",
    "Les jours difficiles passent. Sois doux avec toi-même. 🌸",
    "Une seule tâche à la fois. Tu n'as pas à tout faire. 🤗",
    "Ta seule mission : prendre tes médicaments. Le reste est bonus. 💊"
  ],
  normal: [
    "Bonne journée en perspective ! Un pas à la fois. 🌟",
    "Tu as ce qu'il faut pour réussir aujourd'hui. 💪",
    "Chaque tâche complétée est une victoire. Go ! 🚀"
  ],
  productive: [
    "Tu te sens bien ? Profites-en pour avancer ! 🔥",
    "Journée parfaite pour accomplir des choses ! ⭐",
    "Ton énergie est là, utilise-la sagement ! 💎"
  ]
};

// Hook principal pour le suivi de l'humeur
export function useMoodTracking() {
  const [moodData, setMoodData] = useLocalStorage('adhd-planner-mood', {
    entries: [],
    lastCheckIn: null,
    checkInTime: '08:00',
    reminderEnabled: true
  });

  const [showCheckIn, setShowCheckIn] = useState(false);
  const [todayMood, setTodayMood] = useState(null);
  const [todayEnergy, setTodayEnergy] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const hasCheckedInToday = moodData.lastCheckIn === today;

  // Charger l'humeur du jour si déjà enregistrée
  useEffect(() => {
    if (hasCheckedInToday) {
      const todayEntry = moodData.entries.find(e => e.date === today);
      if (todayEntry) {
        setTodayMood(todayEntry.mood);
        setTodayEnergy(todayEntry.energy);
      }
    }
  }, [hasCheckedInToday, moodData.entries, today]);

  // Afficher le check-in au démarrage si pas fait
  useEffect(() => {
    if (!hasCheckedInToday) {
      const timer = setTimeout(() => {
        setShowCheckIn(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasCheckedInToday]);

  // Enregistrer le check-in avec haptic feedback
  const saveCheckIn = useCallback((mood, energy, note = '') => {
    const entry = {
      date: today,
      mood,
      energy,
      note,
      timestamp: new Date().toISOString()
    };

    setMoodData(prev => ({
      ...prev,
      entries: [entry, ...prev.entries.slice(0, 29)],
      lastCheckIn: today
    }));

    setTodayMood(mood);
    setTodayEnergy(energy);
    setShowCheckIn(false);

    // Haptic feedback pour le check-in
    haptic.moodCheckIn();
  }, [today, setMoodData]);

  // Obtenir le mode actuel
  const currentMode = useMemo(() => {
    if (!todayMood) return 'normal';
    return MOOD_LEVELS[todayMood]?.mode || 'normal';
  }, [todayMood]);

  // Obtenir un message d'encouragement
  const getDailyMessage = useCallback(() => {
    const messages = MOOD_MESSAGES[currentMode] || MOOD_MESSAGES.normal;
    return messages[Math.floor(Math.random() * messages.length)];
  }, [currentMode]);

  // Statistiques de la semaine
  const weeklyStats = useMemo(() => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const weekEntries = moodData.entries.filter(e => e.date >= weekAgo);

    if (weekEntries.length === 0) return null;

    const avgMood = weekEntries.reduce((sum, e) => sum + e.mood, 0) / weekEntries.length;
    const avgEnergy = weekEntries.reduce((sum, e) => sum + e.energy, 0) / weekEntries.length;

    return {
      avgMood: Math.round(avgMood * 10) / 10,
      avgEnergy: Math.round(avgEnergy * 10) / 10,
      entries: weekEntries.length,
      trend: weekEntries.length >= 2
        ? weekEntries[0].mood - weekEntries[weekEntries.length - 1].mood
        : 0
    };
  }, [moodData.entries]);

  const shouldReduceLoad = currentMode === 'gentle';
  const shouldSuggestBonus = currentMode === 'productive';

  return {
    showCheckIn,
    setShowCheckIn,
    todayMood,
    todayEnergy,
    currentMode,
    hasCheckedInToday,
    saveCheckIn,
    getDailyMessage,
    entries: moodData.entries,
    weeklyStats,
    shouldReduceLoad,
    shouldSuggestBonus,
    MOOD_LEVELS,
    ENERGY_LEVELS
  };
}

export default useMoodTracking;
